import type { GroupingSeparator } from 'src/components/genericList/types';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { hashCode } from 'src/filters';
import { MODULAR_IDENTIFIED_GENERIC_ACTIVITY_LIST_COLUMN_VISIBILITY_AND_SORT_ORDER } from 'src/stores/genericActivityList/localStorageKeys';

import {
	ActivityListTypeEnum,
	GenericActivityListColumnNames,
	GenericActivityListColumns,
	MODULE,
	MODULE_LIST_TYPE,
} from 'src/models/genericActivityList';

import { encodeBase64 } from 'src/util/common';
import { buildInstanceScrollSlotKeys } from 'src/util/listInstance/listInstanceScrollSlots';

import type {
	ActiveAggregatePayload,
	ActiveViewPayload,
	AggregateSelectionPayload,
	ColumnVisibilityPayload,
	DateFilterActionPayload,
	FetchBoxesActionPayload,
	FetchUsersActionPayload,
	FilterBoxesActionPayload,
	FilterUsersActionPayload,
	GenericActivityListStoreState,
	GenericListPayload,
	GetBoxesGetterPayload,
	GetUsersGetterPayload,
	GroupingSeparatorPayload,
	HistoryRestoreScrollPayload,
	MultiSelectAggregateIdsPayload,
	MultiSelectAggregatePayload,
	MultiSelectElementStatePayload,
	MultiSelectTypesWithCountPayload,
	PendingFetchScope,
	RestoreScrollPayload,
	RetainSelectionPayload,
	ScrollPayload,
	SelectListItemPayload,
	SelectListItemsPayload,
	UnsubscribeFetchBoxesPayload,
	UnsubscribeFetchUsersPayload,
} from './interfaces';
import type { MultiSelectionElementState } from './types';

const DEFAULT_ACTIVE_MODULE = 'inbox';

export const createDefaultGenericActivityListColumns = (): Array<GenericActivityListColumns> => [
	{
		name: GenericActivityListColumnNames.TIMESTAMP,
		sortOrder: 1,
		translationIdentifier: 'generic_list_column_timestamp',
		visible: true,
	},
	{
		name: GenericActivityListColumnNames.CONTACT,
		sortOrder: 2,
		translationIdentifier: 'generic_list_column_contact',
		visible: true,
	},
	{
		name: GenericActivityListColumnNames.INFORMATION,
		sortOrder: 3,
		translationIdentifier: 'generic_list_column_information',
		visible: true,
	},
	{
		name: GenericActivityListColumnNames.CHANNEL,
		sortOrder: 4,
		translationIdentifier: 'generic_list_column_channel',
		visible: true,
	},
];

const defaultColumnModules = [
	MODULE.INBOX,
	MODULE.AGENDA_LIST,
	MODULE.DOCUMENT_LIST,
	MODULE.FILTER_LIST,
	MODULE.MAIL_LIST,
	MODULE.MESSAGE_LIST,
	MODULE.NOTE_LIST,
	MODULE.NOTEBOOK_LIST,
	MODULE.PHONE_CALL_LIST,
	MODULE.TRASH_LIST,
	MODULE.TASK_LIST,
	MODULE.IDO_HISTORY,
	MODULE.IDO_PRINT,
] as const;

const createDefaultColumnVisibilityAndSortOrder = (): Map<string, Array<GenericActivityListColumns>> =>
	new Map<string, Array<GenericActivityListColumns>>(
		defaultColumnModules.map((moduleName) => [moduleName, createDefaultGenericActivityListColumns()])
	);

const restoreColumnVisibilityAndSortOrder = (): Map<string, Array<GenericActivityListColumns>> => {
	const payload = localStorage.getItem(MODULAR_IDENTIFIED_GENERIC_ACTIVITY_LIST_COLUMN_VISIBILITY_AND_SORT_ORDER);
	if (payload == null) {
		return createDefaultColumnVisibilityAndSortOrder();
	}

	try {
		const parsed = JSON.parse(payload) as Record<string, Array<GenericActivityListColumns>> | null;
		if (parsed == null || typeof parsed !== 'object') {
			return createDefaultColumnVisibilityAndSortOrder();
		}

		const entries = Object.entries(parsed)
			.filter(([, value]) => Array.isArray(value))
			.map(([moduleName, value]): [string, Array<GenericActivityListColumns>] => [
				moduleName,
				value.filter((column) => column.name !== GenericActivityListColumnNames.MODULE_INFORMATION),
			]);
		if (entries.length === 0) {
			return createDefaultColumnVisibilityAndSortOrder();
		}

		const restored = new Map<string, Array<GenericActivityListColumns>>(entries);
		const missingDefaultEntries = defaultColumnModules
			.filter((moduleName) => !restored.has(moduleName))
			.map((moduleName): [string, Array<GenericActivityListColumns>] => [
				moduleName,
				createDefaultGenericActivityListColumns(),
			]);

		return new Map<string, Array<GenericActivityListColumns>>([...restored, ...missingDefaultEntries]);
	} catch {
		return createDefaultColumnVisibilityAndSortOrder();
	}
};

const persistColumnVisibilityAndSortOrder = (
	columnVisibilityAndSortOrder: Map<string, Array<GenericActivityListColumns>>
): void => {
	localStorage.setItem(
		MODULAR_IDENTIFIED_GENERIC_ACTIVITY_LIST_COLUMN_VISIBILITY_AND_SORT_ORDER,
		JSON.stringify(Object.fromEntries(columnVisibilityAndSortOrder))
	);
};

let fetchTokenSequence = 0;

const createDefaultState = (): GenericActivityListStoreState => ({
	activeGenericActivityAggregateId: new Map<string, null | string>(),
	activeModule: DEFAULT_ACTIVE_MODULE,
	activeView: new Map<string, MODULE_LIST_TYPE>(),
	columnVisibilityAndSortOrder: restoreColumnVisibilityAndSortOrder(),
	currentlyActiveDesiredGroupingSeparatorHeader: new Map<string, GroupingSeparator | null>(),
	dateFilterAction: new Map<string, string>(),
	fetchBoxesAction: new Map<string, string>(),
	fetchUsersAction: new Map<string, string>(),
	fetchingResults: false,
	filterBoxesAction: new Map<string, string>(),
	filterUsersAction: new Map<string, string>(),
	firstEntryEvaluated: new Set<string>(),
	getBoxesGetter: new Map<string, string>(),
	getUsersGetter: new Map<string, string>(),
	hasLoadError: false,
	historyRestoreScrollPos: new Map<string, number>(),
	loadedListsByModule: new Map<string, Set<MODULE_LIST_TYPE>>(),
	multiSelection: new Map<string, Record<string, boolean>>(),
	multiSelectionAggregateIds: new Map<string, Array<string>>(),
	multiSelectionElementState: new Map<string, MultiSelectionElementState>(),
	multiSelectionTypesWithCount: new Map<string, Record<string, number>>(),
	pendingFetchScopeByToken: new Map<string, PendingFetchScope>(),
	restoreScrollPos: new Map<string, number>(),
	scrollPos: new Map<string, number>(),
	selectedAggregateIds: new Map<string, Array<string>>(),
	unsubscribeFetchBoxesAction: new Map<string, string>(),
	unsubscribeFetchUsersAction: new Map<string, string>(),
});

export const useGenericActivityListStore = defineStore('genericActivityList', {
	actions: {
		addMultiSelectAggregateId(payload: MultiSelectAggregatePayload): void {
			const aggregateIds = cloneDeep(this.getMultiSelectAggregateIds(payload.genericListIdentifier));
			aggregateIds.push(payload.aggregateId);
			this.setMultiSelectAggregateIds({
				aggregateIds: Array.from(new Set(aggregateIds)),
				genericListIdentifier: payload.genericListIdentifier,
			});
		},
		addSelectedAggregateId(payload: AggregateSelectionPayload): void {
			this.selectedAggregateIds.set(payload.genericListIdentifier, [encodeBase64(payload.selectedAggregateId)]);
		},
		// Task 3.1 (List-Isolation-Teardown): löscht ALLE genericListIdentifier-gekeyten Slices dieses
		// Stores für eine dispose-te Listen-Instanz — Gegenstück zu listInstanceLifecycle.disposeInstance,
		// verhindert monotones Wachstum dieser Maps über die Lebensdauer der App-Session (Leak).
		// Umfasst jede Map, deren Setter/Getter-Signatur genericListIdentifier als Payload-Feld/Parameter
		// führt (GenericListPayload-Vertrag). NICHT enthalten, weil per Vertrag anders gekeyt (Audit-Fund):
		// - columnVisibilityAndSortOrder: alle realen Call-Sites (SettingsLists.vue, GenericActivityListTabPanel.vue,
		//   IdoActivityHistoryList.vue, NotebookList.vue) übergeben moduleRouteName, NICHT die Instanz-ID —
		//   Spalten-Layout ist bewusst modulweit geteilt (User-Präferenz), nicht instanzgebunden; zusätzlich
		//   localStorage-persistiert. Ein genericListIdentifier mit Instanz-Suffix trifft dort nie einen Eintrag.
		// - firstEntryEvaluated/loadedListsByModule: Getter/Setter nehmen explizit `moduleRouteName`
		//   entgegen (anderer Keyspace, session-scoped, bounded durch Modulanzahl — kein Leak).
		beginListFetch(scope: string, listType?: ActivityListTypeEnum): string {
			fetchTokenSequence += 1;
			const fetchToken = `${scope}#${fetchTokenSequence}`;
			this.pendingFetchScopeByToken.set(fetchToken, { listType, scope });
			this.fetchingResults = true;
			this.hasLoadError = false;
			return fetchToken;
		},
		clearForIdentifier(genericListIdentifier: string): void {
			this.activeGenericActivityAggregateId.delete(genericListIdentifier);
			this.activeView.delete(genericListIdentifier);
			this.currentlyActiveDesiredGroupingSeparatorHeader.delete(genericListIdentifier);
			this.dateFilterAction.delete(genericListIdentifier);
			this.fetchBoxesAction.delete(genericListIdentifier);
			this.fetchUsersAction.delete(genericListIdentifier);
			this.filterBoxesAction.delete(genericListIdentifier);
			this.filterUsersAction.delete(genericListIdentifier);
			this.getBoxesGetter.delete(genericListIdentifier);
			this.getUsersGetter.delete(genericListIdentifier);
			this.historyRestoreScrollPos.delete(genericListIdentifier);
			this.multiSelection.delete(genericListIdentifier);
			this.multiSelectionAggregateIds.delete(genericListIdentifier);
			this.multiSelectionElementState.delete(genericListIdentifier);
			this.multiSelectionTypesWithCount.delete(genericListIdentifier);
			this.restoreScrollPos.delete(genericListIdentifier);
			this.scrollPos.delete(genericListIdentifier);
			this.selectedAggregateIds.delete(genericListIdentifier);
			this.unsubscribeFetchBoxesAction.delete(genericListIdentifier);
			this.unsubscribeFetchUsersAction.delete(genericListIdentifier);
		},
		// Task 3.1 (List-Isolation-Teardown, Part-2-Review): löscht die drei view-suffixierten (Open/Mine/All)
		// Scroll-Slot-Keys EINER isolierten Listen-Instanz. clearForIdentifier trifft sie NICHT, weil scrollPos/
		// restoreScrollPos/historyRestoreScrollPos nicht unter genericListIdentifier, sondern unter
		// `${baseVirtualListId}${hashCode(genericListIdentifier)}<Open|Mine|All>` gekeyt sind (s.
		// GenericActivityListTabPanel resolvedVirtualListId → GenericActivityList props.virtualListId). Ohne dies
		// wachsen die drei Maps über Tab-Öffnen/-Schließen-Zyklen monoton (Leak). Klassik: der Panel ruft dies
		// handle-gated NIE für die Default-Instanz auf; zusätzlich tragen Instanz-Keys den hashCode und kollidieren
		// daher ohnehin nie mit den Default-Slots (`${baseVirtualListId}<View>` ohne Hash).
		clearInstanceScrollSlots(baseVirtualListId: string, genericListIdentifier: string): void {
			buildInstanceScrollSlotKeys(baseVirtualListId, genericListIdentifier).forEach((scrollSlotKey) => {
				this.scrollPos.delete(scrollSlotKey);
				this.restoreScrollPos.delete(scrollSlotKey);
				this.historyRestoreScrollPos.delete(scrollSlotKey);
			});
		},
		clearListLoadError(): void {
			this.hasLoadError = false;
		},
		clearScrollSlot(virtualListId: string): void {
			this.scrollPos.delete(virtualListId);
			this.restoreScrollPos.delete(virtualListId);
			this.historyRestoreScrollPos.delete(virtualListId);
		},
		clearTheCurrentlyActiveDesiredGroupingSeparatorHeader(payload: GenericListPayload): void {
			this.currentlyActiveDesiredGroupingSeparatorHeader.set(payload.genericListIdentifier, null);
		},
		clearTheSelection(payload: GenericListPayload): void {
			this.multiSelection.set(payload.genericListIdentifier, {});
			this.multiSelectionAggregateIds.set(payload.genericListIdentifier, []);
			this.multiSelectionElementState.set(payload.genericListIdentifier, 'deactivated');
			this.multiSelectionTypesWithCount.set(payload.genericListIdentifier, {});
		},
		endListFetch(fetchToken: string): void {
			if (!this.pendingFetchScopeByToken.delete(fetchToken)) {
				return;
			}
			this.fetchingResults = this.pendingFetchScopeByToken.size > 0;
		},
		getActiveGenericActivityAggregateId(genericListIdentifier: string): null | string {
			return this.activeGenericActivityAggregateId.get(genericListIdentifier) ?? null;
		},
		getActiveView(genericListIdentifier: string): MODULE_LIST_TYPE {
			return this.activeView.get(genericListIdentifier) ?? MODULE_LIST_TYPE.OPEN;
		},
		getColumnVisibilityAndSortOrder(genericListIdentifier: string): Array<GenericActivityListColumns> {
			return this.columnVisibilityAndSortOrder.get(genericListIdentifier) ?? [];
		},
		getCurrentlyActiveDesiredGroupingSeparatorHeader(genericListIdentifier: string): GroupingSeparator | null {
			return this.currentlyActiveDesiredGroupingSeparatorHeader.get(genericListIdentifier) ?? null;
		},
		getDateFilterAction(genericListIdentifier: string): string {
			return this.dateFilterAction.get(genericListIdentifier) ?? '';
		},
		getFetchFilterBoxesAction(genericListIdentifier: string): string {
			return this.filterBoxesAction.get(genericListIdentifier) ?? '';
		},
		getFetchFilterUsersAction(genericListIdentifier: string): string {
			return this.fetchUsersAction.get(genericListIdentifier) ?? '';
		},
		getFilterBoxesAction(genericListIdentifier: string): string {
			return this.filterBoxesAction.get(genericListIdentifier) ?? '';
		},
		getFilterUsersAction(genericListIdentifier: string): string {
			return this.filterUsersAction.get(genericListIdentifier) ?? '';
		},
		getGetBoxesGetter(genericListIdentifier: string): string {
			return this.getBoxesGetter.get(genericListIdentifier) ?? '';
		},
		getGetUsersGetter(genericListIdentifier: string): string {
			return this.getUsersGetter.get(genericListIdentifier) ?? '';
		},
		getHistoryRestoreScrollPos(genericListIdentifier: string): number {
			return this.historyRestoreScrollPos.get(genericListIdentifier) ?? 0;
		},
		getMultiSelectAggregateIds(genericListIdentifier: string): Array<string> {
			return this.multiSelectionAggregateIds.get(genericListIdentifier) ?? [];
		},
		getMultiSelectElementState(genericListIdentifier: string): MultiSelectionElementState {
			return this.multiSelectionElementState.get(genericListIdentifier) ?? 'deactivated';
		},
		getMultiSelectTypesWithCount(genericListIdentifier: string): Record<string, number> {
			return this.multiSelectionTypesWithCount.get(genericListIdentifier) ?? {};
		},
		getRestoreScrollPos(genericListIdentifier: string): number {
			return this.restoreScrollPos.get(genericListIdentifier) ?? 0;
		},
		getScrollPos(genericListIdentifier: string): number {
			return this.scrollPos.get(genericListIdentifier) ?? 0;
		},
		getSelectedAggregateIds(genericListIdentifier: string): Array<string> {
			return this.selectedAggregateIds.get(genericListIdentifier) ?? [];
		},
		getSelection(genericListIdentifier: string): Record<string, boolean> {
			return this.multiSelection.get(genericListIdentifier) ?? {};
		},
		getUnsubscribeFetchFilterBoxesAction(genericListIdentifier: string): string {
			return this.unsubscribeFetchBoxesAction.get(genericListIdentifier) ?? '';
		},
		getUnsubscribeFetchFilterUsersAction(genericListIdentifier: string): string {
			return this.unsubscribeFetchUsersAction.get(genericListIdentifier) ?? '';
		},
		hasEvaluatedFirstEntry(moduleRouteName: string): boolean {
			return this.firstEntryEvaluated.has(moduleRouteName);
		},
		isFetchingScope(scope: string, listType?: ActivityListTypeEnum): boolean {
			for (const pendingFetch of this.pendingFetchScopeByToken.values()) {
				if (pendingFetch.scope !== scope) {
					continue;
				}
				// A fetch without a lane (single-list stores) and a probe without a lane
				// both match the whole scope; otherwise the lanes must agree.
				if (
					listType === undefined ||
					pendingFetch.listType === undefined ||
					pendingFetch.listType === listType
				) {
					return true;
				}
			}
			return false;
		},
		isListLoaded(moduleRouteName: string, listType: MODULE_LIST_TYPE): boolean {
			return this.loadedListsByModule.get(moduleRouteName)?.has(listType) ?? false;
		},
		markFirstEntryEvaluated(moduleRouteName: string): void {
			this.firstEntryEvaluated.add(moduleRouteName);
		},
		markListLoaded(moduleRouteName: string, listType: MODULE_LIST_TYPE): void {
			const existing = this.loadedListsByModule.get(moduleRouteName);
			if (existing) {
				existing.add(listType);
				return;
			}
			this.loadedListsByModule.set(moduleRouteName, new Set([listType]));
		},
		removeMultiSelectAggregateId(payload: MultiSelectAggregatePayload): void {
			const filteredAggregateIds = this.getMultiSelectAggregateIds(payload.genericListIdentifier).filter(
				(storedAggregateId) => storedAggregateId !== payload.aggregateId
			);
			this.setMultiSelectAggregateIds({
				aggregateIds: Array.from(new Set(filteredAggregateIds)),
				genericListIdentifier: payload.genericListIdentifier,
			});
		},
		removeSelectedAggregateId(payload: AggregateSelectionPayload | string): void {
			if (typeof payload === 'string') {
				return;
			}

			const selectedAggregateIds = this.selectedAggregateIds.get(payload.genericListIdentifier);
			if (selectedAggregateIds == null) {
				return;
			}

			this.selectedAggregateIds.set(
				payload.genericListIdentifier,
				selectedAggregateIds.filter((aggregateId) => aggregateId !== encodeBase64(payload.selectedAggregateId))
			);
		},
		reset(genericListIdentifier: string): void {
			this.activeView.set(genericListIdentifier, MODULE_LIST_TYPE.OPEN);
			this.currentlyActiveDesiredGroupingSeparatorHeader.delete(genericListIdentifier);
			this.fetchBoxesAction.delete(genericListIdentifier);
			this.fetchUsersAction.delete(genericListIdentifier);
			this.filterBoxesAction.delete(genericListIdentifier);
			this.filterUsersAction.delete(genericListIdentifier);
			this.getBoxesGetter.delete(genericListIdentifier);
			this.getUsersGetter.delete(genericListIdentifier);
			this.multiSelection.delete(genericListIdentifier);
			this.multiSelectionElementState.set(genericListIdentifier, 'deactivated');
			this.dateFilterAction.delete(genericListIdentifier);
			this.unsubscribeFetchBoxesAction.delete(genericListIdentifier);
			this.unsubscribeFetchUsersAction.delete(genericListIdentifier);
		},
		resetHistoryScrollPosition(genericListIdentifier: string): void {
			this.historyRestoreScrollPos.delete(genericListIdentifier);
		},
		resetScrollPositions(genericListIdentifier: string): void {
			this.historyRestoreScrollPos.delete(genericListIdentifier);
			this.restoreScrollPos.delete(genericListIdentifier);
			this.scrollPos.delete(genericListIdentifier);

			const cleanGenericListIdentifier = genericListIdentifier
				.replace(/Open/g, '')
				.replace(/All/g, '')
				.replace(/Mine/g, '');

			this.historyRestoreScrollPos.delete(`${cleanGenericListIdentifier}Open`);
			this.historyRestoreScrollPos.delete(`${cleanGenericListIdentifier}All`);
			this.historyRestoreScrollPos.delete(`${cleanGenericListIdentifier}Mine`);
			this.restoreScrollPos.delete(`${cleanGenericListIdentifier}Open`);
			this.restoreScrollPos.delete(`${cleanGenericListIdentifier}All`);
			this.restoreScrollPos.delete(`${cleanGenericListIdentifier}Mine`);
			this.scrollPos.delete(`${cleanGenericListIdentifier}Open`);
			this.scrollPos.delete(`${cleanGenericListIdentifier}All`);
			this.scrollPos.delete(`${cleanGenericListIdentifier}Mine`);
		},
		resetTheScrollPosition(genericListIdentifier: string): void {
			this.restoreScrollPos.delete(genericListIdentifier);
			this.scrollPos.delete(genericListIdentifier);
		},
		retainTheSelectionForTheVisibleItems(payload: RetainSelectionPayload): void {
			if (payload.visibleItems.length === 0) {
				return;
			}

			const selectedAggregateIds = this.getMultiSelectAggregateIds(payload.genericListIdentifier);
			if (selectedAggregateIds.length === 0) {
				return;
			}

			const visibleTypesByAggregateId = new Map(
				payload.visibleItems.map((visibleItem) => [visibleItem.aggregateId, visibleItem.type])
			);
			const retainedAggregateIds = selectedAggregateIds.filter((aggregateId) =>
				visibleTypesByAggregateId.has(aggregateId)
			);

			if (retainedAggregateIds.length === selectedAggregateIds.length) {
				return;
			}

			const retainedSelection: Record<string, boolean> = {};
			const retainedTypesWithCount: Record<string, number> = {};
			retainedAggregateIds.forEach((aggregateId) => {
				retainedSelection[String(hashCode(aggregateId))] = true;
				const visibleType = visibleTypesByAggregateId.get(aggregateId);
				if (visibleType !== undefined) {
					retainedTypesWithCount[visibleType] = (retainedTypesWithCount[visibleType] ?? 0) + 1;
				}
			});

			this.multiSelection.set(payload.genericListIdentifier, retainedSelection);
			this.setMultiSelectAggregateIds({
				aggregateIds: retainedAggregateIds,
				genericListIdentifier: payload.genericListIdentifier,
			});
			this.setMultiSelectTypesWithCount({
				genericListIdentifier: payload.genericListIdentifier,
				multiSelectionTypesWithCount: retainedTypesWithCount,
			});

			if (retainedAggregateIds.length === 0) {
				this.multiSelectionElementState.set(payload.genericListIdentifier, 'deactivated');
			}
		},
		selectListItem(payload: SelectListItemPayload): void {
			const identifier = String(payload.identifier);
			const currentSelection = cloneDeep(this.multiSelection.get(payload.genericListIdentifier) ?? {});
			currentSelection[identifier] = payload.selectionState;
			this.multiSelection.set(payload.genericListIdentifier, currentSelection);

			const multiSelectionTypesWithCount = cloneDeep(
				this.getMultiSelectTypesWithCount(payload.genericListIdentifier)
			);

			if (payload.selectionState) {
				multiSelectionTypesWithCount[payload.type] = (multiSelectionTypesWithCount[payload.type] ?? 0) + 1;
			} else if (payload.type in multiSelectionTypesWithCount) {
				multiSelectionTypesWithCount[payload.type] = multiSelectionTypesWithCount[payload.type] - 1;
			}

			this.setMultiSelectTypesWithCount({
				genericListIdentifier: payload.genericListIdentifier,
				multiSelectionTypesWithCount: multiSelectionTypesWithCount,
			});
		},
		selectListItems(payload: SelectListItemsPayload): void {
			const currentSelection = cloneDeep(this.multiSelection.get(payload.genericListIdentifier) ?? {});
			payload.identifiers.forEach((identifier) => {
				currentSelection[identifier] = payload.selectionState;
			});
			this.multiSelection.set(payload.genericListIdentifier, currentSelection);

			this.setMultiSelectTypesWithCount({
				genericListIdentifier: payload.genericListIdentifier,
				multiSelectionTypesWithCount: payload.multiSelectionTypesWithCount,
			});
		},
		setActiveGenericActivityAggregateId(payload: ActiveAggregatePayload): void {
			this.activeGenericActivityAggregateId.set(payload.genericListIdentifier, payload.aggregateId);
		},
		setActiveModule(activeModule: string): void {
			this.activeModule = activeModule;
		},
		setActiveView(payload: ActiveViewPayload): void {
			const genericListIdentifier = String(payload.genericListIdentifier ?? '');
			if (genericListIdentifier.length === 0) {
				return;
			}

			this.activeView.set(genericListIdentifier, payload.activeView as MODULE_LIST_TYPE);
		},
		setColumnVisibilityAndSortOrder(payload: ColumnVisibilityPayload): void {
			this.columnVisibilityAndSortOrder.set(payload.genericListIdentifier, payload.columnVisibilityAndSortOrder);
			persistColumnVisibilityAndSortOrder(this.columnVisibilityAndSortOrder);
		},
		setDateFilterAction(payload: DateFilterActionPayload): void {
			this.dateFilterAction.set(payload.genericListIdentifier, payload.dateFilterAction);
		},
		setFetchFilterBoxesAction(payload: FetchBoxesActionPayload): void {
			this.fetchBoxesAction.set(payload.genericListIdentifier, payload.fetchBoxesAction);
		},
		setFetchFilterUsersAction(payload: FetchUsersActionPayload): void {
			this.fetchUsersAction.set(payload.genericListIdentifier, payload.fetchUsersAction);
		},

		setFilterBoxesAction(payload: FilterBoxesActionPayload): void {
			this.filterBoxesAction.set(payload.genericListIdentifier, payload.filterBoxesAction);
		},
		setFilterUsersAction(payload: FilterUsersActionPayload): void {
			this.filterUsersAction.set(payload.genericListIdentifier, payload.filterUsersAction);
		},
		setGetFilterBoxesGetter(payload: GetBoxesGetterPayload): void {
			this.getBoxesGetter.set(payload.genericListIdentifier, payload.getBoxesGetterAction);
		},
		setGetFilterUsersGetter(payload: GetUsersGetterPayload): void {
			this.getUsersGetter.set(payload.genericListIdentifier, payload.getUsersGetterAction);
		},
		setListLoadError(): void {
			this.hasLoadError = true;
		},
		setMultiSelectAggregateIds(payload: MultiSelectAggregateIdsPayload): void {
			this.multiSelectionAggregateIds.set(payload.genericListIdentifier, payload.aggregateIds);
		},
		setMultiSelectElementState(payload: MultiSelectElementStatePayload): void {
			const genericListIdentifier = String(payload.genericListIdentifier ?? '');
			if (genericListIdentifier.length === 0) {
				return;
			}

			this.multiSelectionElementState.set(genericListIdentifier, payload.multiSelectElementState);
		},
		setMultiSelectTypesWithCount(payload: MultiSelectTypesWithCountPayload): void {
			this.multiSelectionTypesWithCount.set(payload.genericListIdentifier, payload.multiSelectionTypesWithCount);
		},
		setTheCurrentlyActiveDesiredGroupingSeparatorHeader(payload: GroupingSeparatorPayload): void {
			this.currentlyActiveDesiredGroupingSeparatorHeader.set(
				payload.genericListIdentifier,
				payload.currentlyActiveDesiredGroupingSeparatorHeader ?? null
			);
		},
		setTheHistoryRestoreScrollPos(payload: HistoryRestoreScrollPayload): void {
			this.historyRestoreScrollPos.set(payload.genericListIdentifier, payload.historyRestoreScrollPos);
		},
		setTheRestoreScrollPos(payload: RestoreScrollPayload): void {
			this.restoreScrollPos.set(payload.genericListIdentifier, payload.restoreScrollPos);
		},
		setTheScrollPos(payload: ScrollPayload | number): void {
			if (typeof payload === 'number') {
				return;
			}

			this.scrollPos.set(payload.genericListIdentifier, payload.scrollPos);
		},
		setUnsubscribeFetchFilterBoxesAction(payload: UnsubscribeFetchBoxesPayload): void {
			this.unsubscribeFetchBoxesAction.set(payload.genericListIdentifier, payload.unsubscribeFetchBoxesAction);
		},
		setUnsubscribeFetchFilterUsersAction(payload: UnsubscribeFetchUsersPayload): void {
			this.unsubscribeFetchUsersAction.set(payload.genericListIdentifier, payload.unsubscribeFetchUsersAction);
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
